import { useEffect, useMemo, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  CatalogAutocomplete,
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { getEnvironmentsFromEntity } from './envUtils';

class EntityEnvironmentFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  filterEntity(entity: Entity): boolean {
    const envs = getEnvironmentsFromEntity(entity);
    return this.values.some(v => envs.includes(v));
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

type EnvironmentEntityFilters = DefaultEntityFilters & {
  environments?: EntityEnvironmentFilter;
};

export const EnvironmentPicker = () => {
  const {
    updateFilters,
    backendEntities,
    queryParameters: { environments: envParameter },
  } = useEntityList<EnvironmentEntityFilters>();

  const queryParamEnvironments = useMemo(
    () => [envParameter].flat().filter(Boolean) as string[],
    [envParameter],
  );

  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    queryParamEnvironments,
  );

  // Restore from the URL query params when they change (e.g. on first load).
  useEffect(() => {
    if (queryParamEnvironments.length) {
      setSelectedOptions(queryParamEnvironments);
    }
  }, [queryParamEnvironments]);

  const availableOptions = useMemo(() => {
    const all = new Set<string>();
    backendEntities.forEach(entity =>
      getEnvironmentsFromEntity(entity).forEach(env => all.add(env)),
    );
    return [...all].sort((a, b) => a.localeCompare(b));
  }, [backendEntities]);

  useEffect(() => {
    updateFilters({
      environments: selectedOptions.length
        ? new EntityEnvironmentFilter(selectedOptions)
        : undefined,
    });
  }, [selectedOptions, updateFilters]);

  if (!availableOptions.length) {
    return null;
  }

  return (
    <CatalogAutocomplete<string, true>
      label="Environments"
      name="environments-picker"
      multiple
      disableCloseOnSelect
      options={availableOptions}
      value={selectedOptions}
      onChange={(_event, options) => setSelectedOptions(options)}
    />
  );
};
